import { Component, OnDestroy, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { IonicModule } from "@ionic/angular";
import { Subscription } from "rxjs";
import { PLATFORM_ADAPTER } from "../../core/platform/platform.adapter";
import { AuthService } from "../../services/auth/auth.service";
import { ClientTokenService } from "../../services/auth/client-token.service";
import { MissionInterface, MissionService } from "../../services/mission/mission.service";
import {
  EMPTY_POPUP_MEMORY,
  dismissMissionPopup,
  missionPopups,
  missionSummary,
} from "../../services/mission/mission-popups";

// One popup per open mission, over every page. It closes after a minute, on
// dismiss, or when the mission is acknowledged anywhere. The dismiss is kept in
// memory only, so it holds for this tab, iframe or WebView until a reload.
@Component({
  selector: "app-mission-popup",
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: "./mission-popup.component.html",
  styleUrls: ["./mission-popup.component.scss"],
})
export class MissionPopupComponent implements OnDestroy {
  private missions = inject(MissionService);
  private router = inject(Router);
  private auth = inject(AuthService);
  private clientToken = inject(ClientTokenService);
  private platform = inject(PLATFORM_ADAPTER);

  protected visible: MissionInterface[] = [];
  protected readonly summary = missionSummary;
  private open: MissionInterface[] = [];
  private memory = EMPTY_POPUP_MEMORY;
  private tick?: ReturnType<typeof setInterval>;
  private sub: Subscription = this.missions.open$.subscribe((open) => {
    this.open = open;
    this.refresh();
    if (open.length && !this.tick) this.tick = setInterval(() => this.refresh(), 1000);
    if (!open.length && this.tick) {
      clearInterval(this.tick);
      this.tick = undefined;
    }
  });

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    clearInterval(this.tick);
  }

  protected byId = (_: number, m: MissionInterface) => m.id;

  protected dismiss(m: MissionInterface, ev?: Event): void {
    ev?.stopPropagation();
    this.memory = dismissMissionPopup(this.memory, m.id);
    this.refresh();
  }

  protected show(m: MissionInterface): void {
    this.dismiss(m);
    this.missions.focus$.next(m.id);
    // The list is on the briefing tab only; the weather and documents tabs
    // are under /flow-briefing too.
    if (this.router.url !== "/flow-briefing") {
      this.router.navigateByUrl("/flow-briefing").catch((err) => console.error("navigate: ", err));
    }
  }

  private refresh(): void {
    const loggedIn = this.platform.isNative ? this.auth.authLogin_user : !!this.clientToken.getTokenSync();
    // The minute starts when a popup can be seen: not before login, not in a
    // hidden tab, and never on the med-crew wallboard, which a tap would
    // navigate away from its mission view.
    const shown =
      loggedIn &&
      !this.router.url.startsWith("/med-crew") &&
      (typeof document === "undefined" || document.visibilityState === "visible");
    if (!shown) {
      this.visible = [];
      return;
    }
    const next = missionPopups(this.open, this.memory, Date.now());
    this.memory = next.memory;
    this.visible = next.visible;
  }
}
